import { useState } from "react";
import api, { formatEur, formatDate } from "@/lib/api";
import {
  X, Upload, Loader2, CheckCircle2, FileSpreadsheet, Plus, Merge, EyeOff,
  AlertTriangle, Sparkles, Receipt,
} from "lucide-react";

const ACTION_STYLES = {
  create: "bg-emerald-50 text-emerald-800 border-emerald-300",
  merge: "bg-blue-50 text-blue-800 border-blue-300",
  skip: "bg-stone-100 text-stone-500 border-stone-300",
};

export default function HenrriJournalModal({ onClose, onDone }) {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [recognized, setRecognized] = useState([]);
  const [ambiguous, setAmbiguous] = useState([]);
  const [toCreate, setToCreate] = useState([]);
  const [avoirs, setAvoirs] = useState([]);
  const [err, setErr] = useState("");
  const [committing, setCommitting] = useState(false);
  const [activeTab, setActiveTab] = useState("recognized");

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setLoading(true); setErr(""); setPreview(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/import/henrri-journal/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setRecognized(data.recognized.map((g) => ({ ...g, action: "merge", project_id: g.matched_project_id })));
      setAmbiguous(data.ambiguous.map((g) => ({ ...g, action: "ambiguous", project_id: null })));
      setToCreate(data.to_create.map((g) => ({ ...g, action: "create" })));
      setAvoirs(data.avoirs.map((a) => ({ ...a, skip: false })));
      // Default to ambiguous tab if there are items to validate
      if (data.ambiguous.length > 0) setActiveTab("ambiguous");
      else if (data.to_create.length > 0) setActiveTab("to_create");
    } catch (e) {
      setErr(e.response?.data?.detail || "Erreur d'import");
    } finally { setLoading(false); }
  };

  const updateInList = (list, setList, i, patch) => setList(list.map((x, idx) => idx === i ? { ...x, ...patch } : x));

  const commit = async () => {
    setCommitting(true); setErr("");
    try {
      const { data } = await api.post("/import/henrri-journal/confirm", {
        import_id: preview.import_id,
        recognized: recognized.map((g) => stripGroup(g)),
        ambiguous: ambiguous.map((g) => stripGroup(g)),
        to_create: toCreate.map((g) => stripGroup(g)),
        avoirs: avoirs.map((a) => ({ ...a })),
      });
      onDone(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Erreur de validation");
    } finally { setCommitting(false); }
  };

  if (!preview) {
    return (
      <Wrapper onClose={onClose}>
        <Header onClose={onClose}/>
        <div className="mt-6 p-8 border-2 border-dashed border-stone-300 rounded-2xl text-center">
          <Sparkles size={28} className="mx-auto text-stone-400 mb-2"/>
          <p className="text-sm text-stone-600 mb-4">Téléchargez votre <strong>Journal des ventes Henrri</strong> (XLSX) — détection automatique des projets, clients, paiements et avoirs.</p>
          <label className="inline-flex bg-stone-900 hover:bg-black text-white px-4 py-2 rounded-lg text-sm font-semibold gap-2 cursor-pointer">
            {loading ? <Loader2 size={16} className="animate-spin"/> : <Upload size={16}/>}
            {loading ? "Analyse en cours…" : "Choisir le fichier XLSX"}
            <input type="file" accept=".xlsx" onChange={onPick} className="hidden" disabled={loading} data-testid="henrri-journal-input"/>
          </label>
          {err && <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded mt-4 px-3 py-2">{err}</div>}
        </div>
      </Wrapper>
    );
  }

  const totalAction = recognized.length + ambiguous.filter((g) => g.action !== "ambiguous" && g.action !== "skip").length + toCreate.filter((g) => g.action === "create").length;
  const totalDocs = [...recognized, ...ambiguous, ...toCreate].filter((g) => g.action !== "skip" && g.action !== "ambiguous").reduce((s, g) => s + g.docs.length, 0);
  const remainingAmbiguous = ambiguous.filter((g) => g.action === "ambiguous").length;

  return (
    <Wrapper onClose={onClose}>
      <Header onClose={onClose}/>
      <KpiBar stats={preview.stats} recognized={recognized.length} ambiguous={remainingAmbiguous} toCreate={toCreate.length} avoirs={avoirs.length}/>

      <div className="mt-4 flex items-center gap-3 flex-wrap p-3 rounded-lg bg-stone-50 border border-stone-200 text-sm" data-testid="hj-recap">
        <span><span className="font-bold text-emerald-700">{totalAction}</span> opération(s) projet · <span className="font-bold">{totalDocs}</span> document(s) finance · <span className="font-bold text-stone-700">{avoirs.filter((a) => !a.skip).length}</span> avoir(s)</span>
        <button
          data-testid="hj-validate"
          onClick={commit}
          disabled={committing || (totalAction === 0 && avoirs.every((a) => a.skip))}
          className="ml-auto bg-stone-900 hover:bg-black text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
        >
          {committing ? <Loader2 size={14} className="animate-spin"/> : <CheckCircle2 size={14}/>}
          Valider l'import
        </button>
      </div>

      {err && <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded mt-3 px-3 py-2">{err}</div>}

      {/* Tabs */}
      <div className="mt-4 flex gap-2 flex-wrap border-b border-stone-200">
        <Tab id="recognized" active={activeTab} setActive={setActiveTab} count={recognized.length}>✅ Reconnus</Tab>
        <Tab id="ambiguous" active={activeTab} setActive={setActiveTab} count={ambiguous.length} accent="amber">⚠️ Ambigus</Tab>
        <Tab id="to_create" active={activeTab} setActive={setActiveTab} count={toCreate.length} accent="emerald">🆕 À créer</Tab>
        <Tab id="avoirs" active={activeTab} setActive={setActiveTab} count={avoirs.length}>↩️ Avoirs</Tab>
      </div>

      <div className="mt-3 space-y-2 max-h-[55vh] overflow-y-auto" data-testid="hj-rows">
        {activeTab === "recognized" && recognized.map((g, i) => (
          <GroupRow key={`rec-${i}-${g.project_id || g.client || ""}`} g={g} idx={i} data-testid={`hj-recognized-${i}`}
                    onUpdate={(p) => updateInList(recognized, setRecognized, i, p)}
                    actions={g.already_imported ? ["skip"] : ["merge", "skip"]}
                    alreadyImported={g.already_imported}/>
        ))}
        {activeTab === "recognized" && recognized.length === 0 && <Empty msg="Aucun projet déjà reconnu — votre journal est neuf 🚀"/>}

        {activeTab === "ambiguous" && ambiguous.map((g, i) => (
          <AmbiguousRow key={`amb-${i}-${g.client || ""}`} g={g} idx={i}
                        onUpdate={(p) => updateInList(ambiguous, setAmbiguous, i, p)}/>
        ))}
        {activeTab === "ambiguous" && ambiguous.length === 0 && <Empty msg="Aucun cas ambigu — tout est clair ✨"/>}

        {activeTab === "to_create" && toCreate.map((g, i) => (
          <GroupRow key={`new-${i}-${g.client || ""}`} g={g} idx={i} data-testid={`hj-tocreate-${i}`}
                    onUpdate={(p) => updateInList(toCreate, setToCreate, i, p)}
                    actions={["create", "skip"]}/>
        ))}
        {activeTab === "to_create" && toCreate.length === 0 && <Empty msg="Aucun nouveau projet à créer."/>}

        {activeTab === "avoirs" && avoirs.map((a, i) => (
          <AvoirRow key={`av-${i}-${a.numero || a.client || ""}`} a={a} idx={i} onUpdate={(p) => updateInList(avoirs, setAvoirs, i, p)}/>
        ))}
        {activeTab === "avoirs" && avoirs.length === 0 && <Empty msg="Aucun avoir détecté."/>}
      </div>
    </Wrapper>
  );
}

function stripGroup(g) {
  return {
    action: g.action,
    project_id: g.project_id,
    matched_project_id: g.matched_project_id,
    raison_sociale: g.raison_sociale,
    enseigne: g.enseigne,
    ville: g.ville,
    client: g.client,
    docs: g.docs,
    ca_total: g.ca_total,
    ca_paye: g.ca_paye,
  };
}

function Wrapper({ children, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-6xl w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="henrri-journal-modal">
        {children}
      </div>
    </div>
  );
}

function Header({ onClose }) {
  return (
    <div className="flex items-center justify-between mb-1">
      <div>
        <h3 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2"><FileSpreadsheet size={22}/> Import journal Henrri</h3>
        <p className="text-sm text-stone-500">Détection intelligente projets / clients / paiements / avoirs.</p>
      </div>
      <button onClick={onClose} data-testid="henrri-journal-close" className="text-stone-500 hover:text-stone-900"><X size={18}/></button>
    </div>
  );
}

function KpiBar({ stats, recognized, ambiguous, toCreate, avoirs }) {
  const items = [
    ["Ventes", stats.ventes_total],
    ["Paiements", stats.paiements_total],
    ["Impayés", stats.impayes_total],
    ["Reconnus", recognized, "text-emerald-700"],
    ["Ambigus", ambiguous, "text-amber-700"],
    ["À créer", toCreate, "text-stone-900"],
    ["Avoirs", avoirs, "text-stone-500"],
  ];
  return (
    <div className="mt-4 grid grid-cols-3 md:grid-cols-7 gap-2" data-testid="hj-kpi">
      {items.map(([l, v, c]) => (
        <div key={l} className="bg-white border border-stone-200 rounded-lg p-2 text-center">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{l}</div>
          <div className={`font-mono text-lg font-bold ${c || ""}`}>{v}</div>
        </div>
      ))}
    </div>
  );
}

function Tab({ id, active, setActive, count, children, accent }) {
  const isActive = active === id;
  const accentCls = accent === "amber" && count > 0 ? "border-amber-500 text-amber-800" :
                    accent === "emerald" && count > 0 ? "border-emerald-600 text-emerald-800" :
                    "border-stone-900 text-stone-900";
  return (
    <button
      data-testid={`hj-tab-${id}`}
      onClick={() => setActive(id)}
      className={`px-3 py-2 text-xs font-semibold flex items-center gap-2 border-b-2 transition ${isActive ? accentCls : "border-transparent text-stone-500 hover:text-stone-800"}`}
    >
      {children}
      <span className={`pill text-[10px] ${isActive ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600"}`}>{count}</span>
    </button>
  );
}

function GroupRow({ g, idx, onUpdate, actions, alreadyImported }) {
  return (
    <div className={`border rounded-lg p-3 ${g.action === "skip" && !alreadyImported ? "opacity-50" : ""}`} data-testid={`hj-group-${idx}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-1">
          {alreadyImported && <span className="pill bg-emerald-50 text-emerald-700 text-[10px]">Déjà importé</span>}
          {actions.map((a) => (
            <button
              key={a}
              data-testid={`hj-action-${idx}-${a}`}
              onClick={() => onUpdate({ action: a })}
              className={`px-2 py-1 rounded border text-[10px] font-semibold flex items-center gap-1 ${g.action === a ? ACTION_STYLES[a] : "border-stone-200 text-stone-500"}`}
            >
              {a === "create" && <><Plus size={10}/> Créer projet</>}
              {a === "merge" && <><Merge size={10}/> Fusionner</>}
              {a === "skip" && <><EyeOff size={10}/> Ignorer</>}
            </button>
          ))}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold">{g.raison_sociale}</div>
          <div className="text-xs text-stone-500">
            {g.enseigne || "—"}{g.ville ? ` · ${g.ville}` : ""} · client {g.client || "—"}
            {g.matched_project_nom && <span className="text-blue-700"> · ↪ {g.matched_project_nom}</span>}
          </div>
        </div>
        <div className="text-right">
          <div className="text-sm font-mono font-bold">{formatEur(g.ca_total)}</div>
          <div className="text-xs text-emerald-700">payé {formatEur(g.ca_paye)}</div>
        </div>
      </div>
      <DocsList docs={g.docs}/>
    </div>
  );
}

function AmbiguousRow({ g, idx, onUpdate }) {
  const isMerge = g.action === "merge";
  const isCreate = g.action === "create";
  const isSkip = g.action === "skip";
  return (
    <div className={`border rounded-lg p-3 ${isSkip ? "opacity-50" : "border-amber-200 bg-amber-50/30"}`} data-testid={`hj-ambig-${idx}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <AlertTriangle size={16} className="text-amber-600 mt-1 shrink-0"/>
        <div className="flex-1 min-w-0">
          <div className="font-semibold">{g.raison_sociale}</div>
          <div className="text-xs text-stone-500">{g.enseigne || "—"}{g.ville ? ` · ${g.ville}` : ""} · client {g.client || "—"} · <span className="font-mono">{formatEur(g.ca_total)}</span></div>
          <div className="text-xs text-amber-800 mt-1">Plusieurs projets candidats — choisissez :</div>
          <div className="mt-2 space-y-1">
            {g.candidates.map((c) => (
              <label key={c.project_id} className={`flex items-center gap-2 p-2 rounded border text-xs cursor-pointer ${isMerge && g.project_id === c.project_id ? "border-blue-500 bg-blue-50" : "border-stone-200"}`}>
                <input
                  type="radio"
                  name={`ambig-${idx}`}
                  data-testid={`hj-ambig-${idx}-merge-${c.project_id}`}
                  checked={isMerge && g.project_id === c.project_id}
                  onChange={() => onUpdate({ action: "merge", project_id: c.project_id })}
                />
                <span className="flex-1">
                  <span className="font-semibold">{c.nom}</span>
                  <span className="text-stone-500"> · {c.client || "—"}</span>
                </span>
                <span className="pill bg-stone-100 text-stone-600 text-[10px]">score {c.score}</span>
              </label>
            ))}
            <label className={`flex items-center gap-2 p-2 rounded border text-xs cursor-pointer ${isCreate ? "border-emerald-500 bg-emerald-50" : "border-stone-200"}`}>
              <input
                type="radio"
                name={`ambig-${idx}`}
                data-testid={`hj-ambig-${idx}-create`}
                checked={isCreate}
                onChange={() => onUpdate({ action: "create", project_id: null })}
              />
              <Plus size={12}/> <span>Conserver séparé — créer un nouveau projet</span>
            </label>
            <label className={`flex items-center gap-2 p-2 rounded border text-xs cursor-pointer ${isSkip ? "border-stone-400 bg-stone-100" : "border-stone-200"}`}>
              <input
                type="radio"
                name={`ambig-${idx}`}
                data-testid={`hj-ambig-${idx}-skip`}
                checked={isSkip}
                onChange={() => onUpdate({ action: "skip", project_id: null })}
              />
              <EyeOff size={12}/> <span>Ignorer ce groupe</span>
            </label>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-xs text-emerald-700">payé {formatEur(g.ca_paye)}</div>
        </div>
      </div>
      <DocsList docs={g.docs}/>
    </div>
  );
}

function AvoirRow({ a, idx, onUpdate }) {
  return (
    <div className={`border rounded-lg p-3 ${a.skip ? "opacity-50" : ""}`} data-testid={`hj-avoir-${idx}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <Receipt size={16} className="text-stone-500"/>
        <div className="flex-1 min-w-0">
          <div className="font-semibold flex items-center gap-2">{a.numero}<span className="pill bg-stone-100 text-stone-700 text-[10px]">avoir</span></div>
          <div className="text-xs text-stone-500">{a.raison_sociale} · client {a.client} · {formatDate(a.date_emission)}</div>
          {a.linked_facture_numero && a.linked_facture_numero !== a.numero && (
            <div className="text-xs text-blue-700">↪ Lié à la facture <span className="font-mono">{a.linked_facture_numero}</span></div>
          )}
        </div>
        <div className="text-right">
          <div className="text-sm font-mono font-bold text-red-700">{formatEur(a.montant_ttc)}</div>
          <div className="text-[10px] text-stone-500">non comptabilisé en CA</div>
        </div>
        <label className="text-xs flex items-center gap-1 cursor-pointer">
          <input type="checkbox" data-testid={`hj-avoir-${idx}-skip`} checked={a.skip} onChange={(e) => onUpdate({ skip: e.target.checked })}/>
          Ignorer
        </label>
      </div>
    </div>
  );
}

function DocsList({ docs }) {
  if (!docs?.length) return null;
  return (
    <details className="mt-2">
      <summary className="text-xs text-stone-500 cursor-pointer">{docs.length} document(s)</summary>
      <table className="w-full text-xs mt-2">
        <thead className="text-stone-500 uppercase tracking-wider">
          <tr><th className="text-left pr-2">N°</th><th className="text-left pr-2">Type</th><th className="text-left pr-2">Date</th><th className="text-right pr-2">TTC</th><th className="text-left">Statut</th></tr>
        </thead>
        <tbody>
          {docs.map((d) => (
            <tr key={d.numero}>
              <td className="font-mono pr-2">{d.numero}</td>
              <td className="pr-2">{d.type}</td>
              <td className="pr-2">{formatDate(d.date_emission)}</td>
              <td className="text-right font-mono pr-2">{formatEur(d.montant_ttc)}</td>
              <td><span className={`pill text-[10px] ${d.status === "paye" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{d.status}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}

function Empty({ msg }) {
  return <div className="text-center text-stone-400 py-10 text-sm">{msg}</div>;
}
